import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Editor from "@monaco-editor/react";
import axios from "axios";
import { 
  PlayIcon, 
  ArrowDownTrayIcon, 
  ArrowsPointingOutIcon, 
  ShareIcon,
  DocumentIcon,
  CommandLineIcon,
  CircleStackIcon,
  ChevronLeftIcon
} from "@heroicons/react/24/outline";
import API_BASE_URL from "../Config/config";

/* ---------- CONFIG ---------- */
const languageOptions = [
  { label: "C", value: "c" },
  { label: "C++", value: "cpp" },
  { label: "Python", value: "python" },
  { label: "Java", value: "java" },
  { label: "Node.js", value: "nodejs" },
  { label: "JavaScript", value: "javascript" },
  { label: "PHP", value: "php" },
  { label: "Ruby", value: "ruby" },
];

const defaultCodeSnippets = {
  c: `#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}`,
  cpp: `#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello, World!" << endl;\n    return 0;\n}`,
  python: `print('Hello, World!')`,
  java: `public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}`,
  nodejs: `console.log('Hello, Node.js!');`,
  javascript: `console.log('Hello, World!');`,
  php: `<?php\necho "Hello, World!";\n?>`,
  ruby: `puts "Hello, World!"`,
};

const fileExtensions = {
  c: "c",
  cpp: "cpp",
  python: "py",
  java: "java",
  nodejs: "js",
  javascript: "js",
  php: "php",
  ruby: "rb",
};

const monacoLangMap = {
  c: "c",
  cpp: "cpp",
  python: "python",
  java: "java",
  nodejs: "javascript",
  javascript: "javascript",
  php: "php",
  ruby: "ruby",
};

const Compiler = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState("python");
  const [code, setCode] = useState(defaultCodeSnippets["python"]);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const runCode = async () => {
    setIsRunning(true);
    setOutput("Running execution...");
    try {
      const pistonLang = language === "nodejs" ? "javascript" : language;
      const res = await axios.post(`${API_BASE_URL}/api/execute`, {
        language: pistonLang,
        code,
        input,
      });
      const rawOut =
        (res.data.output && res.data.output.trim()) ||
        (res.data.stderr && res.data.stderr.trim()) ||
        "No output";
      setOutput(rawOut);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      if (errMsg.toLowerCase().includes("whitelist-only") || errMsg.toLowerCase().includes("restricted")) {
        setOutput("Execution failed: The Public Compiler API is currently restricted. Please contact support.");
      } else {
        setOutput("Execution failed: " + errMsg);
      }
    } finally {
      setIsRunning(false);
    }
  };

  const downloadCode = () => {
    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `main.${fileExtensions[language] || "txt"}`;
    a.click();
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullScreen(false);
      }
    }
  };

  return (
    <div className={`flex flex-col h-screen bg-[#1E1E1E] text-gray-300 font-sans ${isFullScreen ? 'fixed inset-0 z-[60]' : ''}`}>
      {/* Premium Toolbar */}
      <header className="h-14 bg-[#2D2D2D] border-b border-[#3E3E3E] flex items-center justify-between px-4 shadow-lg relative z-10">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-[#3E3E3E] rounded-lg transition-colors text-gray-400 hover:text-white flex items-center gap-1 group"
            title="Go Back"
          >
            <ChevronLeftIcon className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#5B21B6] flex items-center justify-center shadow-lg shadow-purple-900/20">
              <CommandLineIcon className="h-5 w-5 text-white" />
            </div>
            <span className="text-sm font-black text-white uppercase tracking-widest hidden sm:block">Compiler</span>
          </div>
          
          <div className="h-6 w-px bg-[#3E3E3E]"></div>
          
          <div className="flex items-center gap-2">
            <select 
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                setCode(defaultCodeSnippets[e.target.value]);
              }}
              className="bg-[#3E3E3E] text-xs font-bold text-white border-none rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#5B21B6] outline-none transition-all cursor-pointer hover:bg-[#454545]"
            >
              {languageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={downloadCode} className="p-2 hover:bg-[#3E3E3E] rounded-lg transition-colors text-gray-400 hover:text-white" title="Download">
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
          <button onClick={toggleFullScreen} className="p-2 hover:bg-[#3E3E3E] rounded-lg transition-colors text-gray-400 hover:text-white" title="Fullscreen">
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </button>
          <div className="h-6 w-px bg-[#3E3E3E] mx-1"></div>
          <button 
            onClick={runCode}
            disabled={isRunning}
            className={`
              flex items-center gap-2 px-6 py-2 rounded-xl font-bold text-sm transition-all shadow-lg
              ${isRunning 
                ? 'bg-gray-600 cursor-not-allowed text-gray-400' 
                : 'bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white hover:scale-105 active:scale-95 shadow-purple-900/20'
              }
            `}
          >
            {isRunning ? (
              <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <PlayIcon className="h-4 w-4 fill-current" />
            )}
            {isRunning ? "Running..." : "Run"}
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        {/* Editor & Input/Output Split */}
        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden bg-[#1E1E1E]">
          {/* Main Editor Section */}
          <div className="flex-1 flex flex-col border-r border-[#2D2D2D] relative group">
             {/* Tab Bar */}
             <div className="h-10 bg-[#252526] flex items-center px-4 gap-2">
                <div className="flex items-center gap-2 px-4 h-full bg-[#1E1E1E] border-t-2 border-[#5B21B6] text-white text-[11px] font-bold rounded-t-lg">
                   <DocumentIcon className="h-3 w-3 text-purple-400" />
                   main.{fileExtensions[language]}
                </div>
             </div>
             
             <div className="flex-1 relative">
                <Editor
                  language={monacoLangMap[language]}
                  value={code}
                  theme="vs-dark"
                  onChange={(val) => setCode(val || "")}
                  options={{
                    fontSize: 15,
                    fontFamily: "'JetBrains Mono', monospace",
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    padding: { top: 20 },
                    cursorBlinking: "smooth",
                    lineHeight: 24,
                    smoothScrolling: true,
                  }}
                />
             </div>
          </div>

          {/* Input & Output Section */}
          <div className="w-full lg:w-[400px] bg-[#252526] flex flex-col border-l border-[#3E3E3E] shadow-2xl relative z-10">
             {/* Input Box */}
             <div className="flex-1 flex flex-col min-h-0">
                <div className="h-10 px-6 flex items-center gap-2 border-b border-[#3E3E3E] bg-[#2D2D2D]">
                   <CircleStackIcon className="h-4 w-4 text-purple-400" />
                   <span className="text-[10px] font-black text-white uppercase tracking-widest">Input</span>
                </div>
                <textarea
                  className="flex-1 bg-transparent p-6 text-sm font-mono text-gray-300 outline-none resize-none placeholder-gray-600 focus:bg-[#2A2A2D] transition-colors"
                  placeholder="Program inputs go here..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
             </div>

             {/* Output Box */}
             <div className="flex-1 flex flex-col min-h-0 border-t border-[#3E3E3E]">
                <div className="h-10 px-6 flex items-center justify-between border-b border-[#3E3E3E] bg-[#2D2D2D]">
                   <div className="flex items-center gap-2">
                      <CommandLineIcon className="h-4 w-4 text-green-400" />
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">Output</span>
                   </div>
                   <button 
                    onClick={() => setOutput("")}
                    className="text-[10px] font-black text-gray-500 hover:text-white uppercase transition-colors"
                   >
                    Clear
                   </button>
                </div>
                <div className="flex-1 p-6 text-sm font-mono overflow-y-auto bg-[#1E1E1E]">
                   {output ? (
                     <pre className={`${output.includes('failed') ? 'text-red-400' : 'text-green-400'} whitespace-pre-wrap leading-relaxed`}>
                        {output}
                     </pre>
                   ) : (
                     <p className="text-gray-600 italic">Execution results will appear here...</p>
                   )}
                </div>
             </div>
          </div>
        </div>

        {/* Status Bar */}
        <footer className="h-6 bg-[#5B21B6] flex items-center justify-between px-4 text-[10px] font-bold text-white uppercase tracking-widest">
           <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                 <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                 Connected
              </span>
              <span>{languageOptions.find(o => o.value === language)?.label}</span>
           </div>
           <div className="flex items-center gap-6">
              <span>Ln {code.split('\n').length}</span>
              <span>Col {code.length}</span>
              <span>UTF-8</span>
              <span className="hidden sm:inline">Spaces: 4</span>
           </div>
        </footer>
      </div>
    </div>
  );
};

export default Compiler;





